import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Upload,
  Info,
  Check,
  Save,
  ChevronLeft,
  ChevronRight,
  Plus,
} from 'lucide-react';
import { DIDCreation } from './DIDCreation';
import { countries, countriesAndCities } from '../data/countries-cities';
import { DOG_BREED_LABELS, CAT_BREED_LABELS } from '../data/breeds';
import { usePets } from '../context/PetsContext';
import { useAuth } from '../../../context/AuthContext';
import type { Pet } from '../types/pet';
import {
  CreatePetRequest,
  savePetDraft as savePetDraftApi,
  getPetDraft as getPetDraftApi,
  deletePetDraft as deletePetDraftApi,
  getPetById as getPetByIdApi,
  updatePet as updatePetApi,
  initPetPhotoUpload,
  uploadPetPhoto,
  PetDraft,
} from '../../../api/pets';
import { getApiErrorMessage } from '../../../api/axios';
import { useTranslation } from 'react-i18next';
import petsoPaw from '../assets/78751f73824316be9d04898afd4169ed5ee341b1.png';
import { toast } from 'sonner';

interface PetOnboardingProps {
  onClose: () => void;
  initiatedBy?: 'owner' | 'professional';
  /** When true, after adding a pet the user can add another or close (e.g. from sidebar). Default true. */
  allowMultiple?: boolean;
  /** When 'edit', form is pre-filled from editPet and submit updates the pet instead of creating. No DID, no Add another. */
  mode?: 'create' | 'edit';
  /** Required when mode is 'edit'. Pet to edit (from Overview). */
  editPet?: Pet;
}

interface OnboardingData {
  // Step 1: Basic Identity
  petName: string;
  species: string;
  breed: string;
  dateOfBirth: string;
  approximateAge: string;
  gender: string;
  neuteredSpayed: string;
  color: string;
  petPhoto: File | null;
  imageUrl?: string;
  photoKey?: string;
  motherDID: string; // Changed from parentDID
  fatherDID: string; // Added separate field
  country: string;
  city: string;
  customCity: string; // For manual entry when "Other" is selected

  // Step 2: Ownership & Consent
  primaryOwner: string;
  secondaryOwners: string[];
  coOwners: string[];
  shareHealthData: boolean;
  shareGroomingNotes: boolean;
  emergencyAccess: boolean;

  // Step 3: Health & Medical
  medicalConditions: string;
  allergies: string;
  currentMedications: string;
  dietaryRequirements: string;
  vetClinic: string;
  medicalDocuments: File[];

  // Step 4: Lifestyle & Care
  temperament: string[];
  groomingTolerance: string;
  trainingLevel: string;
  exerciseNeeds: string;
  boardingSuitability: string;
}

const initialData: OnboardingData = {
  petName: '',
  species: '',
  breed: '',
  dateOfBirth: '',
  approximateAge: '',
  gender: '',
  neuteredSpayed: '',
  color: '',
  petPhoto: null,
  motherDID: '',
  fatherDID: '',
  country: '',
  city: '',
  customCity: '',
  primaryOwner: '',
  secondaryOwners: [],
  coOwners: [],
  shareHealthData: true,
  shareGroomingNotes: true,
  emergencyAccess: true,
  medicalConditions: '',
  allergies: '',
  currentMedications: '',
  dietaryRequirements: '',
  vetClinic: '',
  medicalDocuments: [],
  temperament: [],
  groomingTolerance: '',
  trainingLevel: '',
  exerciseNeeds: '',
  boardingSuitability: '',
};

function resolveCityFields(country: string, city: string) {
  const normalizedCity = city.trim();
  if (!normalizedCity) {
    return { city: '', customCity: '' };
  }

  if (normalizedCity === 'Other') {
    return { city: 'Other', customCity: '' };
  }

  const knownCities = country ? countriesAndCities[country] || [] : [];
  if (knownCities.includes(normalizedCity)) {
    return { city: normalizedCity, customCity: '' };
  }

  return { city: 'Other', customCity: normalizedCity };
}

/** Build secondaryOwnerEmails and secondaryOwnerRoles from both Secondary Owners and Co-Parent lists so all appear in Ownership & Care. */
function buildSecondaryOwnersPayload(
  secondaryOwners: string[],
  coOwners: string[]
): Pick<
  import('../../../api/pets').CreatePetRequest,
  'secondaryOwnerEmails' | 'secondaryOwnerRoles'
> {
  const seen = new Set<string>();
  const deduped: string[] = [];
  for (const e of [...secondaryOwners, ...(coOwners || [])]) {
    const trimmed = e.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(trimmed);
  }
  if (deduped.length === 0)
    return { secondaryOwnerEmails: undefined, secondaryOwnerRoles: undefined };
  const roles: Record<string, 'CO_OWNER' | 'CARETAKER'> = {};
  deduped.forEach((email) => {
    roles[email] = 'CO_OWNER';
  });
  return {
    secondaryOwnerEmails: deduped,
    secondaryOwnerRoles: roles,
  };
}

export function PetOnboarding({
  onClose,
  initiatedBy = 'owner',
  allowMultiple = true,
  mode = 'create',
  editPet,
}: PetOnboardingProps) {
  const isEditMode = mode === 'edit' && editPet;
  const {
    addPet,
    addPetToApi,
    updatePetDid,
    updatePet,
    pets,
    setSelectedPetId,
    refreshDraft,
    refreshPets,
  } = usePets();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<OnboardingData>(initialData);
  const [isLoadingDraft, setIsLoadingDraft] = useState(true);
  const [, setRewardInfo] = useState<{
    amount: number;
    message?: string;
  } | null>(null);
  const stepContentRef = useRef<HTMLDivElement>(null);

  // When entering Ownership & Consent (step 2), scroll content to top so user starts at top
  useEffect(() => {
    if (currentStep === 2 && stepContentRef.current) {
      stepContentRef.current.scrollTop = 0;
    }
  }, [currentStep]);

  // Load edit pet data when in edit mode
  useEffect(() => {
    if (!isEditMode || !editPet) return;
    const p = editPet;
    const resolvedCity = resolveCityFields(p.country || '', p.city || '');
    const fromPet: Partial<OnboardingData> = {
      petName: p.name,
      species: p.species,
      breed: p.breed,
      dateOfBirth: p.dateOfBirth || '',
      gender: p.gender,
      color: p.color || '',
      country: p.country || '',
      city: resolvedCity.city,
      customCity: resolvedCity.customCity,
      primaryOwner: p.owner?.name || '',
      motherDID: p.parents?.mother || '',
      fatherDID: p.parents?.father || '',
      imageUrl: p.imageUrl,
      photoKey: p.photoKey,
    };
    setFormData((prev) => ({ ...prev, ...fromPet }));
    getPetByIdApi(editPet.id)
      .then(({ pet: fullPet }) => {
        const ext = fullPet as Record<string, unknown>;
        const neuteredFromApi =
          (ext.neuteredSpayed as boolean | undefined) ??
          (ext.neutered as boolean | undefined);
        setFormData((prev) => ({
          ...prev,
          ...fromPet,
          primaryOwner:
            (fullPet.ownershipOverview?.primaryOwner?.fullName as string) ||
            prev.primaryOwner,
          secondaryOwners:
            (fullPet.secondaryOwnerEmails as string[]) || prev.secondaryOwners,
          neuteredSpayed:
            neuteredFromApi === true
              ? 'yes'
              : neuteredFromApi === false
                ? 'no'
                : prev.neuteredSpayed,
          shareHealthData:
            (ext.shareHealthData as boolean) ?? prev.shareHealthData,
          shareGroomingNotes:
            (ext.shareGroomingNotes as boolean) ?? prev.shareGroomingNotes,
          emergencyAccess:
            (ext.emergencyAccess as boolean) ?? prev.emergencyAccess,
          medicalConditions:
            (ext.medicalConditions as string) || prev.medicalConditions,
          allergies: (ext.allergies as string) || prev.allergies,
          currentMedications:
            (ext.currentMedications as string) || prev.currentMedications,
          dietaryRequirements:
            (ext.dietaryRequirements as string) || prev.dietaryRequirements,
          vetClinic: (ext.vetClinic as string) || prev.vetClinic,
          temperament: Array.isArray(ext.temperament)
            ? (ext.temperament as string[])
            : prev.temperament,
          groomingTolerance:
            (ext.groomingTolerance as string) || prev.groomingTolerance,
          trainingLevel: (ext.trainingLevel as string) || prev.trainingLevel,
          exerciseNeeds: (ext.exerciseNeeds as string) || prev.exerciseNeeds,
          boardingSuitability:
            (ext.boardingSuitability as string) || prev.boardingSuitability,
        }));
      })
      .catch(() => {})
      .finally(() => setIsLoadingDraft(false));
  }, [isEditMode, editPet?.id]);

  // Load draft from API on mount (create mode only)
  useEffect(() => {
    if (isEditMode) return;
    const loadDraft = async () => {
      try {
        const { draft } = await getPetDraftApi();
        if (draft) {
          // Restore form data from draft
          setFormData((prev) => ({
            ...prev,
            petName: draft.petName || '',
            species: draft.species || '',
            breed: draft.breed || '',
            dateOfBirth: draft.dateOfBirth || '',
            approximateAge: draft.approximateAge || '',
            gender: draft.gender || '',
            neuteredSpayed: draft.neuteredSpayed || '',
            color: draft.color || '',
            motherDID: draft.motherDID || '',
            fatherDID: draft.fatherDID || '',
            country: draft.country || '',
            city: draft.city || '',
            customCity: draft.customCity || '',
            primaryOwner: draft.primaryOwner || prev.primaryOwner,
            secondaryOwners: draft.secondaryOwners || [],
            shareHealthData: draft.shareHealthData ?? true,
            shareGroomingNotes: draft.shareGroomingNotes ?? true,
            emergencyAccess: draft.emergencyAccess ?? true,
            medicalConditions: draft.medicalConditions || '',
            allergies: draft.allergies || '',
            currentMedications: draft.currentMedications || '',
            dietaryRequirements: draft.dietaryRequirements || '',
            vetClinic: draft.vetClinic || '',
            temperament: draft.temperament || [],
            groomingTolerance: draft.groomingTolerance || '',
            trainingLevel: draft.trainingLevel || '',
            exerciseNeeds: draft.exerciseNeeds || '',
            boardingSuitability: draft.boardingSuitability || '',
            // File objects can't be stored in DB
            petPhoto: null,
            medicalDocuments: [],
          }));
          if (draft.currentStep && draft.currentStep > 1) {
            setCurrentStep(draft.currentStep);
          }
          if (draft.petName) {
            toast.info(`Draft restored for "${draft.petName}"`, {
              description: 'Your previous progress has been loaded.',
              duration: 4000,
            });
          }
        }
      } catch (e) {
        console.error('Failed to load draft from server:', e);
        // Non-critical error, continue without draft
      } finally {
        setIsLoadingDraft(false);
      }
    };
    loadDraft();
  }, []);

  // Auto-fill primary owner from user data
  useEffect(() => {
    if (user?.name && !formData.primaryOwner) {
      setFormData((prev) => ({ ...prev, primaryOwner: user.name }));
    }
  }, [user?.name]);

  const [isDraft, setIsDraft] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [showDIDCreation, setShowDIDCreation] = useState(false);
  const [showAddAnotherPrompt, setShowAddAnotherPrompt] = useState(false);
  const [createdPetId, setCreatedPetId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const totalSteps = 5;

  const updateField = (field: keyof OnboardingData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear validation errors when user starts typing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  // Validation functions for each step
  const validateStep1 = (): string[] => {
    const errors: string[] = [];
    if (!formData.petName.trim())
      errors.push(t('validation.petNameRequired', 'Pet name is required'));
    if (!formData.species)
      errors.push(t('validation.speciesRequired', 'Species is required'));
    if (!formData.breed.trim())
      errors.push(t('validation.breedRequired', 'Breed is required'));
    if (!formData.gender)
      errors.push(t('validation.genderRequired', 'Gender is required'));
    if (!formData.neuteredSpayed)
      errors.push(
        t(
          'validation.neuteredSpayedRequired',
          'Neutered/Spayed information is required'
        )
      );
    if (!formData.country)
      errors.push(t('validation.countryRequired', 'Country is required'));
    return errors;
  };

  const validateCurrentStep = (): boolean => {
    let errors: string[] = [];

    switch (currentStep) {
      case 1:
        errors = validateStep1();
        break;
      // Other steps don't have required fields
      default:
        break;
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleNext = () => {
    if (!validateCurrentStep()) {
      return; // Don't proceed if validation fails
    }

    if (currentStep < totalSteps) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSaveDraft = async () => {
    if (isSavingDraft) return;

    setIsSavingDraft(true);
    try {
      // Create draft data to save to API
      const draftData: Partial<PetDraft> = {
        petName: formData.petName,
        species: formData.species,
        breed: formData.breed,
        dateOfBirth: formData.dateOfBirth,
        approximateAge: formData.approximateAge,
        gender: formData.gender,
        neuteredSpayed: formData.neuteredSpayed,
        color: formData.color,
        motherDID: formData.motherDID,
        fatherDID: formData.fatherDID,
        country: formData.country,
        city: formData.city,
        customCity: formData.customCity,
        primaryOwner: formData.primaryOwner,
        secondaryOwners: formData.secondaryOwners,
        shareHealthData: formData.shareHealthData,
        shareGroomingNotes: formData.shareGroomingNotes,
        emergencyAccess: formData.emergencyAccess,
        medicalConditions: formData.medicalConditions,
        allergies: formData.allergies,
        currentMedications: formData.currentMedications,
        dietaryRequirements: formData.dietaryRequirements,
        vetClinic: formData.vetClinic,
        temperament: formData.temperament,
        groomingTolerance: formData.groomingTolerance,
        trainingLevel: formData.trainingLevel,
        exerciseNeeds: formData.exerciseNeeds,
        boardingSuitability: formData.boardingSuitability,
        currentStep,
      };

      await savePetDraftApi(draftData);
      // Refresh draft in context so sidebar updates
      await refreshDraft();
      setIsDraft(true);
      toast.success('Draft saved successfully!', {
        description: 'You can continue later from where you left off.',
      });
      setTimeout(() => setIsDraft(false), 2000);
    } catch (e) {
      console.error('Failed to save draft:', e);
      toast.error('Failed to save draft. Please try again.');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const clearDraft = async () => {
    try {
      await deletePetDraftApi();
      // Refresh draft in context so sidebar updates
      await refreshDraft();
    } catch (e) {
      console.error('Failed to clear draft:', e);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      let photoKey = formData.photoKey;

      const petData: CreatePetRequest = {
        name: formData.petName,
        species: formData.species as
          | 'dog'
          | 'cat'
          | 'bird'
          | 'reptile'
          | 'other',
        breed: formData.breed,
        dateOfBirth: formData.dateOfBirth || undefined,
        approximateAge: formData.approximateAge || undefined,
        gender: formData.gender as 'male' | 'female',
        neuteredSpayed: formData.neuteredSpayed === 'yes',
        color: formData.color || undefined,
        country: formData.country || undefined,
        city:
          formData.city === 'Other'
            ? formData.customCity
            : formData.city || undefined,
        motherDid: formData.motherDID || undefined,
        fatherDid: formData.fatherDID || undefined,
        medicalConditions: formData.medicalConditions || undefined,
        allergies: formData.allergies || undefined,
        currentMedications: formData.currentMedications || undefined,
        dietaryRequirements: formData.dietaryRequirements || undefined,
        vetClinic: formData.vetClinic || undefined,
        temperament:
          formData.temperament.length > 0 ? formData.temperament : undefined,
        groomingTolerance: formData.groomingTolerance || undefined,
        trainingLevel: formData.trainingLevel || undefined,
        exerciseNeeds: formData.exerciseNeeds || undefined,
        boardingSuitability: formData.boardingSuitability || undefined,
        shareHealthData: formData.shareHealthData,
        shareGroomingNotes: formData.shareGroomingNotes,
        emergencyAccess: formData.emergencyAccess,
        imageUrl: formData.imageUrl,
        photoKey,
        ...buildSecondaryOwnersPayload(
          formData.secondaryOwners,
          formData.coOwners
        ),
      };

      if (isEditMode && editPet) {
        if (formData.petPhoto) {
          try {
            const contentType =
              formData.petPhoto.type || 'application/octet-stream';
            const initRes = await initPetPhotoUpload({
              petId: editPet.id,
              filename: formData.petPhoto.name,
              contentType,
            });
            await uploadPetPhoto(initRes.key, formData.petPhoto, contentType);
            photoKey = initRes.key;
            petData.photoKey = photoKey;
          } catch (uploadErr: unknown) {
            console.error('Photo upload failed:', uploadErr);
          }
        }
        // Build update payload: keep core identity fixed except Date of Birth, which is now editable.
        const updatePayload: Partial<CreatePetRequest> = {
          name: (formData.petName?.trim() || editPet.name).trim(),
          species: editPet.species as CreatePetRequest['species'],
          breed: editPet.breed,
          gender: editPet.gender as 'male' | 'female',
          dateOfBirth: formData.dateOfBirth || undefined,
          neuteredSpayed: formData.neuteredSpayed === 'yes',
          shareHealthData: formData.shareHealthData,
          shareGroomingNotes: formData.shareGroomingNotes,
          emergencyAccess: formData.emergencyAccess,
        };
        updatePayload.motherDid = formData.motherDID?.trim() || undefined;
        updatePayload.fatherDid = formData.fatherDID?.trim() || undefined;
        if (formData.approximateAge?.trim())
          updatePayload.approximateAge = formData.approximateAge.trim();
        if (formData.color?.trim()) updatePayload.color = formData.color.trim();
        if (formData.country?.trim())
          updatePayload.country = formData.country.trim();
        const cityVal =
          formData.city === 'Other' ? formData.customCity : formData.city;
        if (cityVal?.trim()) updatePayload.city = cityVal.trim();
        if (formData.medicalConditions?.trim())
          updatePayload.medicalConditions = formData.medicalConditions.trim();
        if (formData.allergies?.trim())
          updatePayload.allergies = formData.allergies.trim();
        if (formData.currentMedications?.trim())
          updatePayload.currentMedications = formData.currentMedications.trim();
        if (formData.dietaryRequirements?.trim())
          updatePayload.dietaryRequirements =
            formData.dietaryRequirements.trim();
        if (formData.vetClinic?.trim())
          updatePayload.vetClinic = formData.vetClinic.trim();
        if (formData.temperament?.length)
          updatePayload.temperament = formData.temperament;
        if (formData.groomingTolerance?.trim())
          updatePayload.groomingTolerance = formData.groomingTolerance.trim();
        if (formData.trainingLevel?.trim())
          updatePayload.trainingLevel = formData.trainingLevel.trim();
        if (formData.exerciseNeeds?.trim())
          updatePayload.exerciseNeeds = formData.exerciseNeeds.trim();
        if (formData.boardingSuitability?.trim())
          updatePayload.boardingSuitability =
            formData.boardingSuitability.trim();
        if (formData.imageUrl) updatePayload.imageUrl = formData.imageUrl;
        if (photoKey) updatePayload.photoKey = photoKey;
        const secondaryPayload = buildSecondaryOwnersPayload(
          formData.secondaryOwners,
          formData.coOwners
        );
        updatePayload.secondaryOwnerEmails =
          secondaryPayload.secondaryOwnerEmails ?? [];
        updatePayload.secondaryOwnerRoles =
          secondaryPayload.secondaryOwnerRoles ?? {};
        await updatePetApi(editPet.id, updatePayload);
        updatePet(editPet.id, {
          name: (formData.petName?.trim() || editPet.name).trim(),
          breed: editPet.breed,
          dateOfBirth: formData.dateOfBirth || '',
          gender: editPet.gender,
          color: formData.color || editPet.color,
          country: formData.country ?? editPet.country,
          city:
            formData.city === 'Other'
              ? formData.customCity
              : (formData.city ?? editPet.city),
          parents: {
            mother: formData.motherDID?.trim() || undefined,
            father: formData.fatherDID?.trim() || undefined,
          },
          imageUrl: formData.imageUrl ?? editPet.imageUrl,
          photoKey: photoKey || editPet.photoKey,
        });
        await refreshPets();
        toast.success(
          t('petOverview.editProfileSaved', 'Profile updated successfully.')
        );
        onClose();
        return;
      }

      // Create pet via API
      const result = await addPetToApi(petData);
      const newPetId = result.petId;

      // 3. Upload photo if present
      if (formData.petPhoto && !photoKey) {
        try {
          const contentType =
            formData.petPhoto.type || 'application/octet-stream';
          const initRes = await initPetPhotoUpload({
            petId: newPetId,
            filename: formData.petPhoto.name,
            contentType,
          });

          await uploadPetPhoto(initRes.key, formData.petPhoto, contentType);
          photoKey = initRes.key;
          // Update state so it's available in handleDIDComplete
          setFormData((prev) => ({
            ...prev,
            photoKey,
          }));

          // Sync the new photoKey back to the local pet record in context
          updatePet(newPetId, { photoKey });
        } catch (uploadErr: unknown) {
          console.error('Photo upload failed:', uploadErr);
          // We continue without photo if upload fails during onboarding
        }
      }

      setCreatedPetId(newPetId);
      setRewardInfo(result.reward || null);

      // Show reward toast if applicable
      if (result.reward) {
        toast.success(`+${result.reward.amount} PETS tokens earned!`, {
          description: result.reward.message,
          duration: 5000,
        });
      }

      // Show DID creation flow
      setShowDIDCreation(true);
    } catch (error) {
      console.error('Failed to create pet:', error);
      const message = getApiErrorMessage(
        error,
        'Failed to create pet. Please try again.'
      );
      setSubmitError(message);
      // Do not open DID creation on failure: the API requires a real pet UUID.
      // User stays on the form to retry; only open DID flow after successful create.
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDIDComplete = async (did: string) => {
    console.log('Pet profile and DID created:', { petId: createdPetId, did });

    // Try to update DID via API
    try {
      await updatePetDid(createdPetId, did);
    } catch (error) {
      console.error('Failed to update pet DID via API:', error);
      // Continue anyway - the pet was created, DID update can be retried
    }

    // Create the new pet object for local state
    const newPet: Pet = {
      id: createdPetId,
      did: did,
      name: formData.petName,
      species: formData.species as 'dog' | 'cat',
      breed: formData.breed,
      dateOfBirth: formData.dateOfBirth || formData.approximateAge,
      gender: formData.gender,
      color: formData.color || 'Not specified',
      microchipId: '', // Can be added later
      owner: {
        name: formData.primaryOwner,
        did: 'did:petso:0x1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b', // Current user's DID
      },
      country: formData.country,
      city: formData.city === 'Other' ? formData.customCity : formData.city,
      parents: {
        mother: formData.motherDID,
        father: formData.fatherDID,
      },
      imageUrl: formData.imageUrl,
      photoKey: formData.photoKey,
    };

    // Add pet to local context (in case API call failed)
    addPet(newPet);

    // Select the new pet
    setSelectedPetId(newPet.id);

    // Clear the saved draft since pet was created successfully
    await clearDraft();

    // Award "First Pet Added" reward (silently, non-blocking)
    try {
      const { completeEarningAction } = await import('@/api/rewards');
      await completeEarningAction('r1773697962473');
    } catch { /* Already completed or action not found — ignore */ }

    setShowDIDCreation(false);
    if (allowMultiple) {
      setShowAddAnotherPrompt(true);
    } else {
      onClose();
    }
  };

  const handleAddAnotherPet = () => {
    setFormData({ ...initialData, primaryOwner: user?.name ?? '' });
    setCurrentStep(1);
    setCreatedPetId('');
    setSubmitError(null);
    setValidationErrors([]);
    setShowAddAnotherPrompt(false);
  };

  const steps = [
    t('petOnboarding.steps.basicIdentity', 'Basic Identity'),
    t('petOnboarding.steps.ownershipConsent', 'Ownership & Consent'),
    t('petOnboarding.steps.healthMedical', 'Health & Medical'),
    t('petOnboarding.steps.lifestyleCare', 'Lifestyle & Care'),
    t('petOnboarding.steps.reviewCreate', 'Review & Create'),
  ];

  if (showDIDCreation) {
    return (
      <DIDCreation
        onClose={() => {
          setShowDIDCreation(false);
          onClose();
        }}
        petData={{
          id: createdPetId,
          name: formData.petName,
          species: formData.species,
          breed: formData.breed,
          country: formData.country,
        }}
        onComplete={handleDIDComplete}
      />
    );
  }

  if (showAddAnotherPrompt) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5 sm:p-8 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {t('drawer.petAddedSuccess', 'Pet added successfully')}
          </h3>
          <p className="text-gray-600 mb-8">
            {t(
              'drawer.addAnotherPetQuestion',
              'Would you like to add another pet?'
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              type="button"
              onClick={handleAddAnotherPet}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700"
            >
              <Plus className="w-5 h-5" />
              {t('drawer.addAnotherPet', 'Add another pet')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              {t('drawer.done', 'Done')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-3 sm:p-6">
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            <div>
              <h2 className="text-lg sm:text-2xl mb-1">
                {isEditMode
                  ? t('petOverview.editPetProfile', 'Edit Pet Profile')
                  : t('petOnboarding.createPetProfile', 'Create Pet Profile')}
              </h2>
              <p className="text-sm opacity-90">
                {isEditMode
                  ? t(
                      'petOverview.editPetProfileDesc',
                      "Update your pet's details"
                    )
                  : initiatedBy === 'professional'
                    ? t(
                        'petOnboarding.creatingWithConsent',
                        'Creating profile with owner consent'
                      )
                    : t(
                        'petOnboarding.buildingDigitalIdentity',
                        "Building your pet's digital identity"
                      )}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm sm:text-base transition-colors ${
                      index + 1 < currentStep
                        ? 'bg-green-500'
                        : index + 1 === currentStep
                          ? 'bg-white text-blue-600'
                          : 'bg-white/30'
                    }`}
                  >
                    {index + 1 < currentStep ? (
                      <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                    ) : (
                      <span
                        className={
                          index + 1 === currentStep ? '' : 'text-white'
                        }
                      >
                        {index + 1}
                      </span>
                    )}
                  </div>
                  <span className="text-xs mt-1 sm:mt-2 text-center hidden sm:block">
                    {step}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      index + 1 < currentStep ? 'bg-green-500' : 'bg-white/30'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div ref={stepContentRef} className="flex-1 overflow-auto p-3 sm:p-6">
          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <div className="text-red-600 font-medium text-sm">
                  {t(
                    'petOnboarding.pleaseFillRequired',
                    'Please fill in the required fields:'
                  )}
                </div>
              </div>
              <ul className="mt-2 space-y-1">
                {validationErrors.map((error, index) => (
                  <li
                    key={index}
                    className="text-sm text-red-600 flex items-center gap-2"
                  >
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {currentStep === 1 && (
            <Step1BasicIdentity
              formData={formData}
              updateField={updateField}
              pets={pets}
              readOnlyBasicIdentity={isEditMode}
            />
          )}
          {currentStep === 2 && (
            <Step2OwnershipConsent
              formData={formData}
              updateField={updateField}
            />
          )}
          {currentStep === 3 && (
            <Step3HealthMedical formData={formData} updateField={updateField} />
          )}
          {currentStep === 4 && (
            <Step4LifestyleCare formData={formData} updateField={updateField} />
          )}
          {currentStep === 5 && (
            <Step5Review
              formData={formData}
              onEdit={(step) => setCurrentStep(step)}
            />
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-3 sm:p-6 bg-gray-50">
          {/* Mobile: stack everything vertically. Desktop: single row */}
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center sm:gap-0">
            {!isEditMode ? (
              <button
                onClick={handleSaveDraft}
                disabled={isSavingDraft || isLoadingDraft}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingDraft ? (
                  <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {isSavingDraft
                  ? t('petOnboarding.savingDraft', 'Saving...')
                  : isDraft
                    ? t('petOnboarding.draftSaved', 'Draft Saved!')
                    : t('petOnboarding.saveAsDraft', 'Save as Draft')}
              </button>
            ) : (
              <div className="hidden sm:block" />
            )}

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              {currentStep > 1 && (
                <button
                  onClick={handleBack}
                  className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-2.5 sm:py-3 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-sm sm:text-base"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t('common.back', 'Back')}
                </button>
              )}

              {currentStep < totalSteps ? (
                <button
                  onClick={handleNext}
                  className="flex items-center justify-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity text-sm sm:text-base"
                >
                  {t('common.next', 'Next')}
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <div className="flex flex-col items-stretch sm:items-end gap-2">
                  {submitError && (
                    <p className="text-xs sm:text-sm text-red-600">{submitError}</p>
                  )}
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="flex items-center justify-center gap-1.5 sm:gap-2 px-4 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {isEditMode
                          ? t('common.saving', 'Saving...')
                          : t('petOnboarding.creating', 'Creating...')}
                      </>
                    ) : (
                      <>
                        <Check className="w-5 h-5 flex-shrink-0" />
                        <span>
                        {isEditMode
                          ? t('petOverview.saveChanges', 'Save changes')
                          : t(
                              'petOnboarding.createPetProfileDid',
                              'Create Pet Profile & Generate DID'
                            )}
                        </span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 1: Basic Pet Identity
function Step1BasicIdentity({
  formData,
  updateField,
  pets,
  readOnlyBasicIdentity = false,
}: {
  formData: OnboardingData;
  updateField: (field: keyof OnboardingData, value: unknown) => void;
  pets: Pet[];
  /** When true (edit mode), mandatory identity fields cannot be changed */
  readOnlyBasicIdentity?: boolean;
}) {
  const { t } = useTranslation();
  const ro = readOnlyBasicIdentity;
  const breedSuggestions =
    formData.species === 'dog'
      ? DOG_BREED_LABELS
      : formData.species === 'cat'
        ? CAT_BREED_LABELS
        : [];

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file (JPG, PNG, etc.)');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      updateField('petPhoto', file);
    }
  };

  const handleRemovePhoto = () => {
    updateField('petPhoto', null);
  };

  // Create preview URL for uploaded photo
  const photoPreviewUrl = formData.petPhoto
    ? URL.createObjectURL(formData.petPhoto)
    : null;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl mb-2">
          {t('petOnboarding.step1.title', 'Basic Pet Identity')}
        </h3>
        <p className="text-gray-600">
          {t(
            'petOnboarding.step1.subtitle',
            'Establish core identity details for your pet'
          )}
        </p>
      </div>

      {/* Pet Photo */}
      <div>
        <label className="block text-sm text-gray-600 mb-2">
          {t(
            'petOnboarding.step1.petPhoto',
            'Pet Photo (Optional but Encouraged)'
          )}
        </label>
        <div className="flex items-center gap-4">
          <div className="w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center overflow-hidden relative">
            {photoPreviewUrl ? (
              <img
                src={photoPreviewUrl}
                alt="Pet preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src={petsoPaw}
                alt="Paw"
                className="w-16 h-16"
                style={{ objectFit: 'contain' }}
              />
            )}
          </div>
          <div>
            <input
              type="file"
              id="pet-photo-upload"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
            />
            <label
              htmlFor="pet-photo-upload"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
            >
              <Upload className="w-4 h-4" />
              {formData.petPhoto
                ? t('petOnboarding.step1.changePhoto', 'Change Photo')
                : t('petOnboarding.step1.uploadPhoto', 'Upload Photo')}
            </label>
            {formData.petPhoto && (
              <button
                onClick={handleRemovePhoto}
                className="mt-2 text-sm text-red-600 hover:text-red-700"
              >
                {t('petOnboarding.step1.removePhoto', 'Remove Photo')}
              </button>
            )}
            <p className="text-xs text-gray-500 mt-2">
              {t('petOnboarding.step1.photoNote', 'JPG, PNG up to 5MB')}
            </p>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Pet Name */}
        <div>
          <label className="block text-sm text-gray-600 mb-2">
            {t('petOnboarding.step1.petName', 'Pet Name')} *
            <span className="ml-1 text-xs text-gray-500">
              {t(
                'petOnboarding.step1.petNameHint',
                '(This is how your pet will be identified)'
              )}
            </span>
          </label>
          <input
            type="text"
            value={formData.petName}
            onChange={(e) => updateField('petName', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t(
              'petOnboarding.step1.petNamePlaceholder',
              'Enter pet name'
            )}
          />
        </div>

        {/* Species */}
        <div>
          <label className="block text-sm text-gray-600 mb-2">
            {t('petOnboarding.step1.species', 'Species')} *{' '}
            {ro && (
              <span className="text-xs text-amber-600">
                {t(
                  'petOnboarding.step1.cannotBeChanged',
                  '(cannot be changed)'
                )}
              </span>
            )}
          </label>
          <select
            value={formData.species}
            onChange={(e) => updateField('species', e.target.value)}
            disabled={ro}
            className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${ro ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          >
            <option value="">
              {t('petOnboarding.step1.selectSpecies', 'Select species')}
            </option>
            <option value="dog">{t('petOnboarding.step1.dog', 'Dog')}</option>
            <option value="cat">{t('petOnboarding.step1.cat', 'Cat')}</option>
            <option value="bird">
              {t('petOnboarding.step1.bird', 'Bird')}
            </option>
            <option value="reptile">
              {t('petOnboarding.step1.reptile', 'Reptile')}
            </option>
            <option value="other">
              {t('petOnboarding.step1.other', 'Other')}
            </option>
          </select>
        </div>

        {/* Breed */}
        <div className="relative">
          <label className="block text-sm text-gray-600 mb-2 flex items-center gap-1">
            {t('petOnboarding.step1.breed', 'Breed')} *
            {ro && (
              <span className="text-xs text-amber-600">
                {t(
                  'petOnboarding.step1.cannotBeChanged',
                  '(cannot be changed)'
                )}
              </span>
            )}
            <div className="group relative">
              <Info className="w-3 h-3 text-gray-400 cursor-help" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg">
                {t(
                  'petOnboarding.step1.breedHint',
                  'Helps professionals provide better care'
                )}
              </div>
            </div>
          </label>
          <input
            type="text"
            value={formData.breed}
            onChange={(e) => updateField('breed', e.target.value)}
            list="breed-suggestions"
            readOnly={ro}
            disabled={ro}
            className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${ro ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            placeholder={t(
              'petOnboarding.step1.breedPlaceholder',
              'Enter breed or select Mixed'
            )}
          />
          {breedSuggestions.length > 0 && (
            <datalist id="breed-suggestions">
              {breedSuggestions.map((breed, index) => (
                <option key={index} value={breed} />
              ))}
            </datalist>
          )}
        </div>

        {/* Mother's DID */}
        <div>
          <label className="block text-sm text-gray-600 mb-2 flex items-center gap-1">
            {t('petOnboarding.step1.motherDid', "Mother's DID (Optional)")}
            <div className="group relative">
              <Info className="w-3 h-3 text-gray-400 cursor-help" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                {t(
                  'petOnboarding.step1.motherDidHint',
                  'Choose an existing pet from your Petso profile or enter a DID manually for accurate ancestry tracking'
                )}
              </div>
            </div>
          </label>
          <input
            type="text"
            value={formData.motherDID}
            onChange={(e) => updateField('motherDID', e.target.value)}
            list="mother-did-list"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t(
              'petOnboarding.step1.motherDidPlaceholder',
              'Select from your pets or enter DID manually (e.g. did:petso:...)'
            )}
          />
          <datalist id="mother-did-list">
            {pets
              .filter(
                (p) =>
                  (!formData.species ||
                    p.species.toLowerCase() ===
                      formData.species.toLowerCase()) &&
                  p.gender?.toLowerCase() === 'female' &&
                  p.did
              )
              .map((p) => (
                <option key={p.id} value={p.did || ''}>
                  {p.name} ({p.did})
                </option>
              ))}
          </datalist>
        </div>

        {/* Father's DID */}
        <div>
          <label className="block text-sm text-gray-600 mb-2 flex items-center gap-1">
            {t('petOnboarding.step1.fatherDid', "Father's DID (Optional)")}
            <div className="group relative">
              <Info className="w-3 h-3 text-gray-400 cursor-help" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                {t(
                  'petOnboarding.step1.fatherDidHint',
                  "Enter the father's DID for accurate ancestry tracking in the family tree"
                )}
              </div>
            </div>
          </label>
          <input
            type="text"
            value={formData.fatherDID}
            onChange={(e) => updateField('fatherDID', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t(
              'petOnboarding.step1.fatherDidPlaceholder',
              'e.g., did:petso:max:germanshepherd:f7g8h9i0'
            )}
          />
          <p className="text-xs text-gray-500 mt-1">
            {t(
              'petOnboarding.step1.fatherDidNote',
              'This will be used to build an accurate family tree for your pets'
            )}
          </p>
        </div>

        {/* Date of Birth */}
        <div>
          <label className="block text-sm text-gray-600 mb-2">
            {t('petOnboarding.step1.dateOfBirth', 'Date of Birth (Optional)')}
          </label>
          <input
            type="date"
            value={formData.dateOfBirth}
            onChange={(e) => updateField('dateOfBirth', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Approximate Age */}
        <div>
          <label className="block text-sm text-gray-600 mb-2">
            {t('petOnboarding.step1.approximateAge', 'Or Approximate Age')}
          </label>
          <input
            type="text"
            value={formData.approximateAge}
            onChange={(e) => updateField('approximateAge', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            placeholder="e.g., 2 years"
            disabled={!!formData.dateOfBirth}
          />
        </div>

        {/* Gender */}
        <div>
          <label className="block text-sm text-gray-600 mb-2">
            {t('petOnboarding.step1.gender', 'Gender')} *{' '}
            {ro && (
              <span className="text-xs text-amber-600">
                {t(
                  'petOnboarding.step1.cannotBeChanged',
                  '(cannot be changed)'
                )}
              </span>
            )}
          </label>
          <select
            value={formData.gender}
            onChange={(e) => updateField('gender', e.target.value)}
            disabled={ro}
            className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${ro ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          >
            <option value="">
              {t('petOnboarding.step1.selectGender', 'Select gender')}
            </option>
            <option value="male">
              {t('petOnboarding.step1.male', 'Male')}
            </option>
            <option value="female">
              {t('petOnboarding.step1.female', 'Female')}
            </option>
          </select>
        </div>

        {/* Neutered/Spayed */}
        <div>
          <label className="block text-sm text-gray-600 mb-2">
            {t('petOnboarding.step1.neuteredSpayed', 'Neutered / Spayed')} *
          </label>
          <div className="flex gap-4 mt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="neuteredSpayed"
                value="yes"
                checked={formData.neuteredSpayed === 'yes'}
                onChange={(e) => updateField('neuteredSpayed', e.target.value)}
                className="w-4 h-4 text-blue-600"
              />
              <span>{t('common.yes', 'Yes')}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="neuteredSpayed"
                value="no"
                checked={formData.neuteredSpayed === 'no'}
                onChange={(e) => updateField('neuteredSpayed', e.target.value)}
                className="w-4 h-4 text-blue-600"
              />
              <span>{t('common.no', 'No')}</span>
            </label>
          </div>
        </div>

        {/* Color */}
        <div>
          <label className="block text-sm text-gray-600 mb-2">
            {t('petOnboarding.step1.color', 'Color (Optional)')}
          </label>
          <input
            type="text"
            value={formData.color}
            onChange={(e) => updateField('color', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t(
              'petOnboarding.step1.colorPlaceholder',
              'e.g., Black, White, Brown...'
            )}
          />
        </div>

        {/* Country */}
        <div>
          <label className="block text-sm text-gray-600 mb-2">
            {t('petOnboarding.step1.country', 'Country')} *
          </label>
          <select
            value={formData.country}
            onChange={(e) => {
              updateField('country', e.target.value);
              // Reset city when country changes
              updateField('city', '');
              updateField('customCity', '');
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          >
            <option value="">
              {t('petOnboarding.step1.selectCountry', 'Select country')}
            </option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </div>

        {/* City */}
        <div>
          <label className="block text-sm text-gray-600 mb-2">
            {t('petOnboarding.step1.city', 'City (Optional)')}
          </label>
          <select
            value={formData.city}
            onChange={(e) => {
              updateField('city', e.target.value);
              // Clear custom city if switching from "Other"
              if (e.target.value !== 'Other') {
                updateField('customCity', '');
              }
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            disabled={!formData.country}
          >
            <option value="">
              {formData.country
                ? t('petOnboarding.step1.selectCity', 'Select city (optional)')
                : t(
                    'petOnboarding.step1.selectCountryFirst',
                    'Select country first'
                  )}
            </option>
            {formData.country &&
              countriesAndCities[formData.country]?.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            {formData.country && (
              <option value="Other">
                {t('petOnboarding.step1.other', 'Other')}
              </option>
            )}
          </select>
          {!formData.country && (
            <p className="text-xs text-gray-500 mt-1">
              {t(
                'petOnboarding.step1.pleaseSelectCountryFirst',
                'Please select a country first'
              )}
            </p>
          )}
        </div>

        {/* Custom City Input - shown when "Other" is selected */}
        {formData.city === 'Other' && (
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-600 mb-2">
              {t('petOnboarding.step1.enterCityName', 'Enter City Name')}
            </label>
            <input
              type="text"
              value={formData.customCity}
              onChange={(e) => updateField('customCity', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t(
                'petOnboarding.step1.enterCityNamePlaceholder',
                'Enter your city name'
              )}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t(
                'petOnboarding.step1.enterCityNameNote',
                'Please enter your city name manually'
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Step 2: Ownership & Consent
function Step2OwnershipConsent({
  formData,
  updateField,
}: {
  formData: OnboardingData;
  updateField: (field: keyof OnboardingData, value: unknown) => void;
}) {
  const { t } = useTranslation();
  const [newOwner, setNewOwner] = useState('');
  const [newCoOwner, setNewCoOwner] = useState('');

  const addSecondaryOwner = () => {
    if (newOwner.trim()) {
      updateField('secondaryOwners', [...formData.secondaryOwners, newOwner]);
      setNewOwner('');
    }
  };

  const removeSecondaryOwner = (index: number) => {
    updateField(
      'secondaryOwners',
      formData.secondaryOwners.filter((_: unknown, i: number) => i !== index)
    );
  };

  const addCoOwner = () => {
    if (newCoOwner.trim()) {
      updateField('coOwners', [...(formData.coOwners || []), newCoOwner]);
      setNewCoOwner('');
    }
  };

  const removeCoOwner = (index: number) => {
    updateField(
      'coOwners',
      (formData.coOwners || []).filter((_: unknown, i: number) => i !== index)
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl mb-2">
          {t('petOnboarding.step2.title', 'Ownership & Consent')}
        </h3>
        <p className="text-gray-600">
          {t(
            'petOnboarding.step2.subtitle',
            "Define legal and operational control for your pet's profile"
          )}
        </p>
      </div>

      {/* Primary Owner */}
      <div>
        <label className="block text-sm text-gray-600 mb-2">
          {t('petOnboarding.step2.primaryOwner', 'Primary Owner')}
        </label>
        <input
          type="text"
          value={formData.primaryOwner}
          onChange={(e) => updateField('primaryOwner', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
          readOnly
        />
        <p className="text-xs text-gray-500 mt-1">
          {t(
            'petOnboarding.step2.primaryOwnerNote',
            'Auto-filled from your account'
          )}
        </p>
      </div>

      {/* Secondary Owners */}
      <div>
        <label className="block text-sm text-gray-600 mb-2">
          {t(
            'petOnboarding.step2.secondaryOwners',
            'Secondary Owners / Caretakers (Optional)'
          )}
        </label>
        <div className="flex gap-2 mb-2">
          <input
            type="email"
            value={newOwner}
            onChange={(e) => setNewOwner(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addSecondaryOwner()}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t(
              'petOnboarding.step2.enterEmail',
              'Enter email address'
            )}
          />
          <button
            onClick={addSecondaryOwner}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('petOnboarding.step2.add', 'Add')}
          </button>
        </div>
        {formData.secondaryOwners.length > 0 && (
          <div className="space-y-2">
            {formData.secondaryOwners.map((owner: string, index: number) => (
              <div
                key={index}
                className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-lg"
              >
                <span>{owner}</span>
                <button
                  onClick={() => removeSecondaryOwner(index)}
                  className="text-red-600 hover:text-red-700"
                >
                  {t('petOnboarding.step2.remove', 'Remove')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Consent Toggles */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-6">
        <h4 className="mb-4 flex items-center gap-2">
          <Info className="w-5 h-5 text-blue-600" />
          {t('petOnboarding.step2.dataSharing', 'Data Sharing Permissions')}
        </h4>
        <p className="text-sm text-gray-600 mb-4">
          {t(
            'petOnboarding.step2.dataSharingNote',
            "Control who can access your pet's information. All permissions are granular and revocable at any time."
          )}
        </p>

        {/* Co-Parents - same rights as primary owner */}
        <div className="mb-4 p-4 bg-white rounded-lg border border-blue-200">
          <h5 className="text-sm font-medium text-gray-800 mb-1">
            {t('petOnboarding.step2.coParent', 'Co-Parent')}
          </h5>
          <p className="text-sm text-gray-600 mb-3">
            {t(
              'petOnboarding.step2.coParentDesc',
              'Co-parents have the same rights as the primary owner (e.g. partners, spouses, children). They can manage ownership, permissions, and view or update all pet data.'
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 mb-2">
            <input
              type="email"
              value={newCoOwner}
              onChange={(e) => setNewCoOwner(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addCoOwner()}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t(
                'petOnboarding.step2.enterCoParentEmail',
                'Enter co-parent email'
              )}
            />
            <button
              onClick={addCoOwner}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              {t('petOnboarding.step2.addCoParent', 'Add Co-Parent')}
            </button>
          </div>
          {(formData.coOwners || []).length > 0 && (
            <div className="space-y-2">
              {(formData.coOwners || []).map((email: string, index: number) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-lg"
                >
                  <span>{email}</span>
                  <button
                    onClick={() => removeCoOwner(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    {t('petOnboarding.step2.remove', 'Remove')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.shareHealthData}
              onChange={(e) => updateField('shareHealthData', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded mt-0.5"
            />
            <div>
              <div>
                {t(
                  'petOnboarding.step2.shareHealthData',
                  'Share health data with professionals'
                )}
              </div>
              <div className="text-sm text-gray-600">
                {t(
                  'petOnboarding.step2.shareHealthDataDesc',
                  'Allows vets and medical professionals to access vaccination records, medical history, and health certificates'
                )}
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.shareGroomingNotes}
              onChange={(e) =>
                updateField('shareGroomingNotes', e.target.checked)
              }
              className="w-5 h-5 text-blue-600 rounded mt-0.5"
            />
            <div>
              <div>
                {t(
                  'petOnboarding.step2.shareGroomingNotes',
                  'Share grooming & behavioral notes'
                )}
              </div>
              <div className="text-sm text-gray-600">
                {t(
                  'petOnboarding.step2.shareGroomingNotesDesc',
                  'Enables groomers, trainers, and boarding facilities to provide better personalized care'
                )}
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.emergencyAccess}
              onChange={(e) => updateField('emergencyAccess', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded mt-0.5"
            />
            <div>
              <div>
                {t(
                  'petOnboarding.step2.emergencyAccess',
                  'Emergency access permission'
                )}
              </div>
              <div className="text-sm text-gray-600">
                {t(
                  'petOnboarding.step2.emergencyAccessDesc',
                  'In case of emergency, authorized professionals can access critical health information'
                )}
              </div>
            </div>
          </label>
        </div>

        <div className="mt-4 p-3 bg-white rounded border border-blue-200">
          <div className="text-sm text-gray-700">
            🔒{' '}
            <strong>
              {t('petOnboarding.step2.securityNoteLabel', 'Security Note:')}
            </strong>{' '}
            {t(
              'petOnboarding.step2.securityNote',
              "All consent decisions are logged as part of your pet's DID permissions and are cryptographically secured on the blockchain."
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 3: Health & Medical Snapshot
function Step3HealthMedical({
  formData,
  updateField,
}: {
  formData: OnboardingData;
  updateField: (field: keyof OnboardingData, value: unknown) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl mb-2">
          {t('petOnboarding.step3.title', 'Health & Medical Snapshot')}
        </h3>
        <p className="text-gray-600">
          {t(
            'petOnboarding.step3.subtitle',
            'Establish baseline health data (you can edit this later)'
          )}
        </p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-yellow-600 mt-0.5" />
        <div className="text-sm text-yellow-800">
          {t(
            'petOnboarding.step3.infoNote',
            'This information helps professionals provide better care. All data is encrypted and only shared with your explicit consent.'
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Medical Conditions */}
        <div className="md:col-span-2">
          <label className="block text-sm text-gray-600 mb-2">
            {t(
              'petOnboarding.step3.medicalConditions',
              'Known Medical Conditions'
            )}
          </label>
          <textarea
            value={formData.medicalConditions}
            onChange={(e) => updateField('medicalConditions', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={3}
            placeholder={t(
              'petOnboarding.step3.medicalConditionsPlaceholder',
              'e.g., Hip dysplasia, heart murmur, diabetes...'
            )}
          />
        </div>

        {/* Allergies */}
        <div className="md:col-span-2">
          <label className="block text-sm text-gray-600 mb-2">
            {t('petOnboarding.step3.allergies', 'Allergies')}
          </label>
          <textarea
            value={formData.allergies}
            onChange={(e) => updateField('allergies', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={2}
            placeholder={t(
              'petOnboarding.step3.allergiesPlaceholder',
              'e.g., Chicken, pollen, certain medications...'
            )}
          />
        </div>

        {/* Current Medications */}
        <div>
          <label className="block text-sm text-gray-600 mb-2">
            {t('petOnboarding.step3.currentMedications', 'Current Medications')}
          </label>
          <textarea
            value={formData.currentMedications}
            onChange={(e) => updateField('currentMedications', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={3}
            placeholder={t(
              'petOnboarding.step3.currentMedicationsPlaceholder',
              'List any ongoing medications...'
            )}
          />
        </div>

        {/* Dietary Requirements */}
        <div>
          <label className="block text-sm text-gray-600 mb-2">
            {t(
              'petOnboarding.step3.dietaryRequirements',
              'Dietary Requirements'
            )}
          </label>
          <textarea
            value={formData.dietaryRequirements}
            onChange={(e) => updateField('dietaryRequirements', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={3}
            placeholder={t(
              'petOnboarding.step3.dietaryRequirementsPlaceholder',
              'Special diet, food restrictions...'
            )}
          />
        </div>

        {/* Vet Clinic */}
        <div className="md:col-span-2">
          <label className="block text-sm text-gray-600 mb-2">
            {t('petOnboarding.step3.vetClinic', 'Current Vet Clinic')}
          </label>
          <select
            value={formData.vetClinic}
            onChange={(e) => updateField('vetClinic', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">
              {t(
                'petOnboarding.step3.selectVetClinic',
                'Select vet clinic (when available)'
              )}
            </option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            {t(
              'petOnboarding.step3.vetClinicNote',
              'Pet professionals can register on Petso; once your vet is listed, you can link them here.'
            )}
          </p>
        </div>
      </div>

      {/* Document Upload */}
      <div>
        <label className="block text-sm text-gray-600 mb-2">
          {t(
            'petOnboarding.step3.uploadDocuments',
            'Upload Medical Documents (Optional)'
          )}
        </label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer">
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-2">
            {t(
              'petOnboarding.step3.clickToUpload',
              'Click to upload or drag and drop'
            )}
          </p>
          <p className="text-sm text-gray-500">
            {t(
              'petOnboarding.step3.uploadTypes',
              'Previous medical summary, vet letters, vaccination records'
            )}
          </p>
          <p className="text-xs text-gray-400 mt-2">
            {t(
              'petOnboarding.step3.uploadSizeLimit',
              'PDF, JPG, PNG up to 10MB each'
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

// Step 4: Lifestyle & Care Preferences
function Step4LifestyleCare({
  formData,
  updateField,
}: {
  formData: OnboardingData;
  updateField: (field: keyof OnboardingData, value: unknown) => void;
}) {
  const { t } = useTranslation();
  const temperamentOptions = [
    { key: 'Calm', label: t('petOnboarding.step4.calm', 'Calm') },
    { key: 'Anxious', label: t('petOnboarding.step4.anxious', 'Anxious') },
    {
      key: 'Aggressive',
      label: t('petOnboarding.step4.aggressive', 'Aggressive'),
    },
    { key: 'Social', label: t('petOnboarding.step4.social', 'Social') },
    { key: 'Playful', label: t('petOnboarding.step4.playful', 'Playful') },
    { key: 'Shy', label: t('petOnboarding.step4.shy', 'Shy') },
  ];

  const toggleTemperament = (option: string) => {
    const current = formData.temperament;
    if (current.includes(option)) {
      updateField(
        'temperament',
        current.filter((t: string) => t !== option)
      );
    } else {
      updateField('temperament', [...current, option]);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl mb-2">
          {t('petOnboarding.step4.title', 'Lifestyle & Care Preferences')}
        </h3>
        <p className="text-gray-600">
          {t(
            'petOnboarding.step4.subtitle',
            'Help professionals provide personalized, high-quality service'
          )}
        </p>
      </div>

      {/* Temperament */}
      <div>
        <label className="block text-sm text-gray-600 mb-2">
          {t(
            'petOnboarding.step4.temperament',
            'Temperament (Select all that apply)'
          )}
        </label>
        <div className="flex flex-wrap gap-2">
          {temperamentOptions.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => toggleTemperament(key)}
              className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                formData.temperament.includes(key)
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Grooming Tolerance */}
        <div>
          <label className="block text-sm text-gray-600 mb-2">
            {t('petOnboarding.step4.groomingTolerance', 'Grooming Tolerance')}
            <div className="text-xs text-gray-500 mt-1">
              {t(
                'petOnboarding.step4.groomingToleranceDesc',
                'How does your pet handle grooming?'
              )}
            </div>
          </label>
          <select
            value={formData.groomingTolerance}
            onChange={(e) => updateField('groomingTolerance', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">
              {t(
                'petOnboarding.step4.selectTolerance',
                'Select tolerance level'
              )}
            </option>
            <option value="excellent">
              {t('petOnboarding.step4.excellent', 'Excellent - Loves grooming')}
            </option>
            <option value="good">
              {t('petOnboarding.step4.good', 'Good - Tolerates well')}
            </option>
            <option value="moderate">
              {t('petOnboarding.step4.moderate', 'Moderate - Needs patience')}
            </option>
            <option value="difficult">
              {t(
                'petOnboarding.step4.difficult',
                'Difficult - Requires special handling'
              )}
            </option>
          </select>
        </div>

        {/* Training Level */}
        <div>
          <label className="block text-sm text-gray-600 mb-2">
            {t('petOnboarding.step4.trainingLevel', 'Training Level')}
            <div className="text-xs text-gray-500 mt-1">
              {t(
                'petOnboarding.step4.trainingLevelDesc',
                'Current obedience level'
              )}
            </div>
          </label>
          <select
            value={formData.trainingLevel}
            onChange={(e) => updateField('trainingLevel', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">
              {t(
                'petOnboarding.step4.selectTrainingLevel',
                'Select training level'
              )}
            </option>
            <option value="advanced">
              {t(
                'petOnboarding.step4.advanced',
                'Advanced - Professional training'
              )}
            </option>
            <option value="intermediate">
              {t(
                'petOnboarding.step4.intermediate',
                'Intermediate - Basic commands'
              )}
            </option>
            <option value="basic">
              {t('petOnboarding.step4.basic', 'Basic - Learning fundamentals')}
            </option>
            <option value="untrained">
              {t(
                'petOnboarding.step4.untrained',
                'Untrained - No formal training'
              )}
            </option>
          </select>
        </div>

        {/* Exercise Needs */}
        <div>
          <label className="block text-sm text-gray-600 mb-2">
            {t('petOnboarding.step4.exerciseNeeds', 'Exercise Needs')}
            <div className="text-xs text-gray-500 mt-1">
              {t(
                'petOnboarding.step4.exerciseNeedsDesc',
                'Daily activity requirements'
              )}
            </div>
          </label>
          <select
            value={formData.exerciseNeeds}
            onChange={(e) => updateField('exerciseNeeds', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">
              {t(
                'petOnboarding.step4.selectExerciseNeeds',
                'Select exercise needs'
              )}
            </option>
            <option value="very-high">
              {t('petOnboarding.step4.veryHigh', 'Very High - 2+ hours daily')}
            </option>
            <option value="high">
              {t('petOnboarding.step4.high', 'High - 1-2 hours daily')}
            </option>
            <option value="moderate">
              {t(
                'petOnboarding.step4.moderateExercise',
                'Moderate - 30-60 minutes daily'
              )}
            </option>
            <option value="low">
              {t('petOnboarding.step4.low', 'Low - Light activity')}
            </option>
          </select>
        </div>

        {/* Boarding Suitability */}
        <div>
          <label className="block text-sm text-gray-600 mb-2">
            {t(
              'petOnboarding.step4.boardingSuitability',
              'Boarding Suitability'
            )}
            <div className="text-xs text-gray-500 mt-1">
              {t(
                'petOnboarding.step4.boardingSuitabilityDesc',
                'Comfort with boarding/daycare'
              )}
            </div>
          </label>
          <select
            value={formData.boardingSuitability}
            onChange={(e) => updateField('boardingSuitability', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">
              {t(
                'petOnboarding.step4.selectBoardingSuitability',
                'Select suitability'
              )}
            </option>
            <option value="excellent">
              {t(
                'petOnboarding.step4.excellentBoarding',
                'Excellent - Loves group settings'
              )}
            </option>
            <option value="good">
              {t('petOnboarding.step4.goodBoarding', 'Good - Adapts well')}
            </option>
            <option value="moderate">
              {t(
                'petOnboarding.step4.moderateBoarding',
                'Moderate - Needs time to adjust'
              )}
            </option>
            <option value="not-suitable">
              {t(
                'petOnboarding.step4.notSuitable',
                'Not Suitable - Requires home care'
              )}
            </option>
          </select>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="text-sm text-green-800">
          💡 <strong>{t('petOnboarding.step4.proTipLabel', 'Pro Tip:')}</strong>{' '}
          {t(
            'petOnboarding.step4.proTip',
            "The more detailed your pet's profile, the better professionals can tailor their services. This leads to happier, healthier pets!"
          )}
        </div>
      </div>
    </div>
  );
}

// Step 5: Review & Create
function Step5Review({
  formData,
  onEdit,
}: {
  formData: OnboardingData;
  onEdit: (step: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl mb-2">
          {t('petOnboarding.step5.title', 'Review & Create Pet Profile')}
        </h3>
        <p className="text-gray-600">
          {t(
            'petOnboarding.step5.subtitle',
            "Review all information before creating your pet's digital identity"
          )}
        </p>
      </div>

      {/* Basic Identity */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h4>{t('petOnboarding.step5.basicIdentity', 'Basic Identity')}</h4>
          <button
            onClick={() => onEdit(1)}
            className="text-blue-600 hover:text-blue-700 text-sm"
          >
            {t('petOnboarding.step5.edit', 'Edit')}
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-gray-500">
              {t('petOnboarding.step5.petName', 'Pet Name')}
            </div>
            <div>{formData.petName || '—'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">
              {t('petOnboarding.step5.species', 'Species')}
            </div>
            <div className="capitalize">{formData.species || '—'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">
              {t('petOnboarding.step5.breed', 'Breed')}
            </div>
            <div>{formData.breed || '—'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">
              {t('petOnboarding.step5.dateOfBirth', 'Date of Birth')}
            </div>
            <div>{formData.dateOfBirth || formData.approximateAge || '—'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">
              {t('petOnboarding.step5.gender', 'Gender')}
            </div>
            <div className="capitalize">{formData.gender || '—'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">
              {t('petOnboarding.step5.neuteredSpayed', 'Neutered/Spayed')}
            </div>
            <div className="capitalize">{formData.neuteredSpayed || '—'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">
              {t('petOnboarding.step5.color', 'Color')}
            </div>
            <div>{formData.color || '—'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">
              {t('petOnboarding.step5.country', 'Country')}
            </div>
            <div>{formData.country || '—'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">
              {t('petOnboarding.step5.city', 'City')}
            </div>
            <div>
              {formData.city === 'Other'
                ? formData.customCity || t('petOnboarding.step1.other', 'Other')
                : formData.city || '—'}
            </div>
          </div>
        </div>
      </div>

      {/* Ownership & Consent */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h4>
            {t('petOnboarding.step5.ownershipConsent', 'Ownership & Consent')}
          </h4>
          <button
            onClick={() => onEdit(2)}
            className="text-blue-600 hover:text-blue-700 text-sm"
          >
            {t('petOnboarding.step5.edit', 'Edit')}
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <div className="text-sm text-gray-500">
              {t('petOnboarding.step5.primaryOwner', 'Primary Owner')}
            </div>
            <div>{formData.primaryOwner}</div>
          </div>
          {formData.secondaryOwners.length > 0 && (
            <div>
              <div className="text-sm text-gray-500">
                {t('petOnboarding.step5.secondaryOwners', 'Secondary Owners')}
              </div>
              <div>{formData.secondaryOwners.join(', ')}</div>
            </div>
          )}
          {(formData.coOwners || []).length > 0 && (
            <div>
              <div className="text-sm text-gray-500">
                {t(
                  'petOnboarding.step5.coParents',
                  'Co-Parents (same rights as primary)'
                )}
              </div>
              <div>{(formData.coOwners || []).join(', ')}</div>
            </div>
          )}
          <div className="pt-3 border-t border-gray-200">
            <div className="text-sm text-gray-500 mb-2">
              {t('petOnboarding.step5.permissions', 'Permissions')}
            </div>
            <div className="space-y-1 text-sm">
              <div className="flex items-center gap-2">
                {formData.shareHealthData ? '✅' : '❌'}{' '}
                {t(
                  'petOnboarding.step5.shareHealthData',
                  'Share health data with professionals'
                )}
              </div>
              <div className="flex items-center gap-2">
                {formData.shareGroomingNotes ? '✅' : '❌'}{' '}
                {t(
                  'petOnboarding.step5.shareGroomingNotes',
                  'Share grooming & behavioral notes'
                )}
              </div>
              <div className="flex items-center gap-2">
                {formData.emergencyAccess ? '✅' : '❌'}{' '}
                {t(
                  'petOnboarding.step5.emergencyAccess',
                  'Emergency access permission'
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Health & Medical */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h4>{t('petOnboarding.step5.healthMedical', 'Health & Medical')}</h4>
          <button
            onClick={() => onEdit(3)}
            className="text-blue-600 hover:text-blue-700 text-sm"
          >
            {t('petOnboarding.step5.edit', 'Edit')}
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-gray-500">
              {t('petOnboarding.step5.medicalConditions', 'Medical Conditions')}
            </div>
            <div>
              {formData.medicalConditions ||
                t('petOnboarding.step5.noneSpecified', 'None specified')}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">
              {t('petOnboarding.step5.allergies', 'Allergies')}
            </div>
            <div>
              {formData.allergies ||
                t('petOnboarding.step5.noneSpecified', 'None specified')}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">
              {t(
                'petOnboarding.step5.currentMedications',
                'Current Medications'
              )}
            </div>
            <div>
              {formData.currentMedications ||
                t('petOnboarding.step5.none', 'None')}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">
              {t(
                'petOnboarding.step5.dietaryRequirements',
                'Dietary Requirements'
              )}
            </div>
            <div>
              {formData.dietaryRequirements ||
                t('petOnboarding.step5.none', 'None')}
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="text-sm text-gray-500">
              {t('petOnboarding.step5.vetClinic', 'Vet Clinic')}
            </div>
            <div>
              {formData.vetClinic ||
                t('petOnboarding.step5.notSpecified', 'Not specified')}
            </div>
          </div>
        </div>
      </div>

      {/* Lifestyle & Care */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h4>{t('petOnboarding.step5.lifestyleCare', 'Lifestyle & Care')}</h4>
          <button
            onClick={() => onEdit(4)}
            className="text-blue-600 hover:text-blue-700 text-sm"
          >
            {t('petOnboarding.step5.edit', 'Edit')}
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-gray-500">
              {t('petOnboarding.step5.temperament', 'Temperament')}
            </div>
            <div>
              {formData.temperament.length > 0
                ? formData.temperament.join(', ')
                : t('petOnboarding.step5.notSpecified', 'Not specified')}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">
              {t('petOnboarding.step5.groomingTolerance', 'Grooming Tolerance')}
            </div>
            <div className="capitalize">
              {formData.groomingTolerance ||
                t('petOnboarding.step5.notSpecified', 'Not specified')}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">
              {t('petOnboarding.step5.trainingLevel', 'Training Level')}
            </div>
            <div className="capitalize">
              {formData.trainingLevel ||
                t('petOnboarding.step5.notSpecified', 'Not specified')}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">
              {t('petOnboarding.step5.exerciseNeeds', 'Exercise Needs')}
            </div>
            <div className="capitalize">
              {formData.exerciseNeeds ||
                t('petOnboarding.step5.notSpecified', 'Not specified')}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">
              {t(
                'petOnboarding.step5.boardingSuitability',
                'Boarding Suitability'
              )}
            </div>
            <div className="capitalize">
              {formData.boardingSuitability ||
                t('petOnboarding.step5.notSpecified', 'Not specified')}
            </div>
          </div>
        </div>
      </div>

      {/* DID Generation Notice */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-3 sm:p-6 text-white">
        <h4 className="mb-2">
          {t('petOnboarding.step5.whatHappensNext', 'What Happens Next?')}
        </h4>
        <div className="space-y-2 text-sm opacity-90">
          <div className="flex items-start gap-2">
            <Check className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <span>
              {t(
                'petOnboarding.step5.next1',
                'Pet profile will be created with all the information you provided'
              )}
            </span>
          </div>
          <div className="flex items-start gap-2">
            <Check className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <span>
              {t(
                'petOnboarding.step5.next2',
                'A unique Decentralized Identity (DID) will be generated and secured on the blockchain'
              )}
            </span>
          </div>
          <div className="flex items-start gap-2">
            <Check className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <span>
              {t(
                'petOnboarding.step5.next3',
                'Your pet will be visible across the Petso ecosystem (subject to your permission settings)'
              )}
            </span>
          </div>
          <div className="flex items-start gap-2">
            <Check className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <span>
              {t(
                'petOnboarding.step5.next4',
                'You can start booking appointments and sharing health records immediately'
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
